<template>
  <div class="min-h-screen bg-gradient-to-b from-soft-white to-lavender-tint/30">
    <section class="container mx-auto px-6 pt-10 pb-6 max-w-6xl">
      <p class="text-sm font-semibold text-light-purple uppercase tracking-wide mb-2">
        Always open · Kerala-wide
      </p>
      <h1 class="text-4xl md:text-5xl font-poppins font-bold text-dark-gray mb-4 leading-tight">
        Phlebotomist Jobs in Kerala —
        <span class="text-light-purple">Join Homvisit Network</span>
      </h1>
      <p class="text-lg text-dark-gray/75 max-w-2xl mb-8">
        Corporate health camps and onsite collection teams need reliable phlebos across all districts.
        Apply once. Stay in our active network. We call when work matches your area.
      </p>

      <div class="grid lg:grid-cols-[1.2fr_0.8fr] gap-8 items-start">
        <div class="space-y-6">
          <div class="card-neumorphic !p-6">
            <h2 class="text-xl font-semibold text-dark-gray mb-3">Why phlebos stay with Homvisit</h2>
            <ul class="space-y-3 text-dark-gray/80">
              <li v-for="item in perks" :key="item" class="flex gap-2">
                <CheckCircle :size="18" class="text-light-purple shrink-0 mt-0.5" />
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>

          <div class="card-neumorphic !p-6">
            <h2 class="text-xl font-semibold text-dark-gray mb-3">Open vacancy pages</h2>
            <div class="grid sm:grid-cols-2 gap-3">
              <RouterLink
                v-for="page in vacancyLinks"
                :key="page.slug"
                :to="`/jobs/${page.slug}`"
                class="rounded-2xl bg-soft-white px-4 py-3 text-sm font-medium text-dark-gray hover:text-light-purple border border-light-purple/10"
              >
                {{ page.h1 }}
              </RouterLink>
            </div>
          </div>

          <div class="card-neumorphic !p-6">
            <h2 class="text-xl font-semibold text-dark-gray mb-2">Questions?</h2>
            <p class="text-dark-gray/70 text-sm mb-3">
              Call or WhatsApp before you apply — we reply fast.
            </p>
            <div class="flex flex-wrap gap-3">
              <a :href="`tel:${phoneTel}`" class="btn-neumorphic !py-3 !px-5 text-sm text-light-purple">
                Call {{ phoneDisplay }}
              </a>
              <a
                :href="whatsapp"
                target="_blank"
                rel="noopener"
                class="btn-neumorphic !py-3 !px-5 text-sm text-dark-gray"
              >
                WhatsApp
              </a>
            </div>
          </div>
        </div>

        <JobApplyForm source-page="/jobs" />
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted } from 'vue'
import { CheckCircle } from 'lucide-vue-next'
import JobApplyForm from '../components/JobApplyForm.vue'
import { jobPages } from '../config/jobPages'
import {
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL,
  SITE_URL,
  WHATSAPP_URL,
} from '../config/contact'
import {
  breadcrumbSchema,
  graphSchema,
  jobPostingSchema,
  localBusinessSchema,
  organizationSchema,
  useSeo,
} from '../composables/useSeo'

export default {
  name: 'JobsPage',
  components: { JobApplyForm, CheckCircle },
  setup() {
    const vacancyLinks = jobPages.filter((p) => p.slug.includes('vacancy') || p.slug.includes('salary'))

    useSeo(
      computed(() => ({
        title: 'Phlebotomist Jobs in Kerala | Join Homvisit Network',
        description:
          'Always-open phlebotomist vacancies across Kerala. Join Homvisit for corporate health camps in Kochi, Trivandrum, Calicut, Thrissur and all districts.',
        path: '/jobs',
        keywords: [
          'phlebotomist vacancy in kerala',
          'phlebotomist jobs kerala',
          'phlebotomist vacancy in kochi',
          'phlebotomist salary in kerala',
          'join phlebotomist network kerala',
        ],
        schema: graphSchema([
          organizationSchema(),
          localBusinessSchema(),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Jobs', path: '/jobs' },
          ]),
          jobPostingSchema({
            title: 'Phlebotomist — Homvisit Kerala Network',
            description:
              'Join Homvisit phlebotomist network for corporate health camps and onsite collection across Kerala.',
            url: `${SITE_URL}/jobs`,
            location: 'Kerala',
          }),
        ]),
      }))
    )

    onMounted(() => {
      window.scrollTo(0, 0)
    })

    return {
      perks: [
        'Always-open applications — no waiting for a single listing to appear',
        'Corporate camp & onsite work across 14 Kerala districts',
        'Minimal form — name, phone, district, experience only',
        'We contact you when an assignment matches your location',
      ],
      vacancyLinks,
      phoneDisplay: CONTACT_PHONE_DISPLAY,
      phoneTel: CONTACT_PHONE_TEL,
      whatsapp: WHATSAPP_URL,
    }
  },
}
</script>
